import { env } from '@/config/env';
import { getAuthHeaders } from '@/services/api/client';
import { ENDPOINTS } from './api/endpoints';

export interface StreamOptions {
  message: string;
  conversationId?: string;
  attachment?: {
    kind: 'csv';
    fileName: string;
    content?: string;
  };
  onChunk: (chunk: string) => void;
  onComplete: () => void;
  onError: (error: Error) => void;
}

export interface ChatStreamHandle {
  abort: () => void;
}

function unescapeSSEChunk(payload: string): string {
  return payload.replace(/\\n/g, '\n').replace(/\\r/g, '\r');
}

function concatDataPayload(eventBlock: string): string {
  const lines = eventBlock.split('\n');
  const parts: string[] = [];
  for (const line of lines) {
    if (line.startsWith('data:')) {
      parts.push(line.slice(5).trimStart());
    }
  }
  return parts.join('\n');
}

/** Split complete SSE events (blank-line terminated); keep incomplete tail */
function consumeCompleteEvents(buffer: string): { remainder: string; events: string[] } {
  const events: string[] = [];
  let start = 0;
  while (start < buffer.length) {
    const sep = buffer.indexOf('\n\n', start);
    if (sep === -1) {
      break;
    }
    events.push(buffer.slice(start, sep));
    start = sep + 2;
  }
  return { remainder: buffer.slice(start), events };
}

/**
 * Handle one `data:` payload from FastAPI /chat SSE.
 * @returns true if the consumer should stop (done or fatal error handled)
 */
function handleDataPayload(
  raw: string,
  onChunk: (chunk: string) => void,
  onComplete: () => void,
  onError: (error: Error) => void
): boolean {
  const payload = raw.trimEnd();
  if (!payload) {
    return false;
  }

  if (payload === '[DONE]') {
    onComplete();
    return true;
  }

  try {
    const parsed: unknown = JSON.parse(payload);
    if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      if (typeof obj.error === 'string') {
        onError(new Error(obj.error));
        return true;
      }
      if (typeof obj.content === 'string') {
        onChunk(unescapeSSEChunk(obj.content));
        return false;
      }
    }
  } catch {
    /* plain-text token chunk */
  }

  onChunk(unescapeSSEChunk(payload));
  return false;
}

/**
 * POST /chat — consumes FastAPI SSE (`text/event-stream`). Non-blocking.
 */
export function startChatStream(options: StreamOptions): ChatStreamHandle {
  const { message, conversationId, attachment, onChunk, onComplete, onError } = options;
  const abortController = new AbortController();
  const { signal } = abortController;

  const url = `${env.apiBaseUrl}${ENDPOINTS.CHAT}`;
  const body: Record<string, unknown> = {
    user_response: message,
    conversation_id: conversationId,
  };

  if (attachment) {
    body.attachment = {
      kind: attachment.kind,
      file_name: attachment.fileName,
      ...(attachment.content ? { content: attachment.content } : {}),
    };
  }

  void (async () => {
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
      });
    } catch (e: unknown) {
      if ((e instanceof DOMException || e instanceof Error) && e.name === 'AbortError') {
        return;
      }
      onError(e instanceof Error ? e : new Error('Network error'));
      return;
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      onError(new Error(text || `HTTP ${response.status}`));
      return;
    }

    const reader = response.body?.getReader();
    if (!reader) {
      onError(new Error('No response body'));
      return;
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const split = consumeCompleteEvents(buffer);
        buffer = split.remainder;
        for (const eventBlock of split.events) {
          const payload = concatDataPayload(eventBlock);
          if (handleDataPayload(payload, onChunk, onComplete, onError)) {
            return;
          }
        }
      }

      if (buffer.trim()) {
        const split = consumeCompleteEvents(`${buffer}\n\n`);
        for (const eventBlock of split.events) {
          const payload = concatDataPayload(eventBlock);
          if (handleDataPayload(payload, onChunk, onComplete, onError)) {
            return;
          }
        }
      }
      onComplete();
    } catch (e: unknown) {
      if ((e instanceof DOMException || e instanceof Error) && e.name === 'AbortError') {
        return;
      }
      onError(e instanceof Error ? e : new Error('Stream read error'));
    }
  })();

  return {
    abort: () => abortController.abort(),
  };
}
